import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Drawer, Flex, Space, Typography } from "antd";
import Meta from "antd/es/card/Meta";
import TextArea from "antd/es/input/TextArea";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api";
import { GridMinMax } from "../../../assets/styles/GlobalStyle";
import Loader from "../../../components/Loader";
import UploadFile from "../../../components/UploadFile";
import { EnabledLanguage, TravelPagesWording } from "../../../domain/types";
import { useAppSelector } from "../../../store";
import { AppState } from "../../../store/appSlice";
import AdminLangSwicher from "../../admin_components/AdminLangSwicher";
const { Text } = Typography;

type TravelPagesWordingTranslation = {
  title: string;
  body: string;
  excerpt: string;
  secondTitle: string;
  thirdTitle: string;
  secondExcerpt: string;
  fourthTitle: string;
  thirdExcerpt: string;
};
//
const AdminProductCategoryList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app
  );

  const {
    data: pageTextArry,
    isLoading: isPageTextArrayLoading,
    refetch,
  } = api.useGetTravelFlowPageTextsBySlugQuery("homepage-product-category");

  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [itemID, setItemID] = useState<null | number>(null);

  const [updatedPageTextsState, setUpdatedPageTextsState] = useState<
    Partial<TravelPagesWording>
  >({});

  const onCloseDrawer = () => {
    setDrawerStatus(false);
    setItemID(null);
    setUpdatedPageTextsState({});
  };

  const [updatePageByListItemId, { isLoading: isUpdatePageLoading }] =
    api.useUpdateTravelFlowPageItemByListItemIdMutation();

  const {
    data: existingTravelPageItem,
    isLoading: isExistringTravelPageItemLoading,
    refetch: RefetchExistingItem,
  } = api.useGetTravelFlowPageTextItemByListItemIdQuery(itemID as number, {
    skip: !itemID && true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  // add travel addon fields
  const setFlowTexts = (field: keyof TravelPagesWording, value: any) => {
    setUpdatedPageTextsState({
      ...updatedPageTextsState,
      [field]: value,
    });
  };

  // add travel translation fields
  const setFlowTranslatedField = (
    field: keyof TravelPagesWordingTranslation,
    language: EnabledLanguage,
    value: any
  ) => {
    setUpdatedPageTextsState({
      ...updatedPageTextsState,
      translations: {
        ...updatedPageTextsState.translations,
        [language]: {
          ...updatedPageTextsState?.translations?.[language],
          [field]: value,
        },
      },
    });
  };

  const UpdatePageTexts = async () => {
    try {
      await updatePageByListItemId(updatedPageTextsState).unwrap();
      toast.success(`${t("text")} ${t("updatedSuccessfully")}`);
      RefetchExistingItem();
      onCloseDrawer();
      refetch();
    } catch (error) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0];
        const errorMessage = firstError.message;
        toast.error(errorMessage);
      } else {
        toast.error(`${t("errorOccurred")}`);
      }
    }
  };

  useEffect(() => {
    if (existingTravelPageItem && itemID) {
      setUpdatedPageTextsState(existingTravelPageItem);
    }
  }, [existingTravelPageItem, itemID]);

  const DrawerLoader = isExistringTravelPageItemLoading || isUpdatePageLoading;

  return (
    <>
      <Flex justify="space-between" align="center">
        <Button
          size="large"
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Button>

        <Title level={3}>
          {updatedPageTextsState?.translations?.[field_Language]?.title || ""}
        </Title>

        <AdminLangSwicher />
      </Flex>

      <Divider />

      <GridMinMax>
        {pageTextArry?.map((item, index) => (
          <Card
            onClick={() => [
              setItemID(item?.id as number),
              setDrawerStatus(!drawerStatus),
            ]}
            style={{ height: "100%" }}
            loading={isPageTextArrayLoading}
            key={index}
            hoverable
            cover={
              <img
                style={{
                  height: "100px",
                  objectFit: "contain",
                  marginTop: "20px",
                }}
                alt={item?.image}
                src={item?.image}
              />
            }
          >
            <Meta
              style={{ textAlign: "center" }}
              title={item?.translations?.[field_Language]?.title}
            />
          </Card>
        ))}
      </GridMinMax>

      <Drawer
        extra={<AdminLangSwicher />}
        title={t("update")}
        onClose={onCloseDrawer}
        open={drawerStatus}
      >
        {DrawerLoader ? (
          <Loader />
        ) : (
          <Flex gap={20} vertical>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("title")}</Text>
              <TextArea
                size="large"
                placeholder={t("title")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.title || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "title",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("body")}</Text>
              <TextArea
                size="large"
                placeholder={t("body")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]?.body ||
                  ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "body",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("additionalInformation")}</Text>
              <TextArea
                size="large"
                placeholder={t("additionalInformation")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.excerpt || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "excerpt",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("secondTitle")}</Text>
              <TextArea
                size="large"
                placeholder={t("secondTitle")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.secondTitle || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "secondTitle",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("secondAdditionalInformation")}</Text>
              <TextArea
                size="large"
                placeholder={t("secondAdditionalInformation")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.secondExcerpt || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "secondExcerpt",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("thirdTitle")}</Text>
              <TextArea
                size="large"
                placeholder={t("thirdTitle")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.thirdTitle || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "thirdTitle",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>{t("thirdAdditionalInformation")}</Text>
              <TextArea
                size="large"
                placeholder={t("thirdAdditionalInformation")}
                autoSize={{ minRows: 2 }}
                value={
                  updatedPageTextsState?.translations?.[field_Language]
                    ?.thirdExcerpt || ""
                }
                onChange={(event) =>
                  setFlowTranslatedField(
                    "thirdExcerpt",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Space>

            <UploadFile
              label={t("uploadImage")}
              onChange={(props) => setFlowTexts("image", props.url)}
              defaultFileName={updatedPageTextsState?.image || ""}
            />

            <Divider />

            <Button
              size="large"
              type="primary"
              loading={DrawerLoader}
              onClick={UpdatePageTexts}
            >
              {t("update")}
            </Button>
          </Flex>
        )}
      </Drawer>
    </>
  );
};

export default AdminProductCategoryList;
